import {
    createSlice,
    Slice,
    SliceCaseReducers,
    CreateSliceOptions,
    PayloadAction,
    Reducer,
    Draft,
    ActionReducerMapBuilder,
    AsyncThunk
} from '@reduxjs/toolkit';
import { store } from '.';
import { IErrorResponse } from '@/shared/types/interfaces';
import { retryDeferMap } from '@/shared/helpers/retryRequest';

const getInitialState = <T>(name: string, initialState: T, version: number, nulledFields?: Partial<Record<keyof T, string[]>>) => {
    const initialStateVersionAppended = { ...initialState, version };
    try {
        const value = window.localStorage.getItem(name);

        if (value) {
            if (!Object.keys(value).length || JSON.parse(value)?.version !== version) {
                return initialStateVersionAppended;
            }
            const newData = JSON.parse(value);
            if (nulledFields) {
                Object.entries(nulledFields).forEach(([key, value]) => {
                    (value as string[]).forEach(field => {
                        newData[key][field] = undefined;
                    });
                });
            }
            return newData;
        } else {
            return initialStateVersionAppended;
        }
    } catch {
        window.localStorage.removeItem(name);
        return initialStateVersionAppended;
    }
};

const sliceMap: Record<string, Slice> = {};

export const createPersistSlice = <T, Reducers extends SliceCaseReducers<T>>(
    options: CreateSliceOptions<T, Reducers>,
    nulledFields?: Partial<Record<keyof T, string[]>>
): Slice<T, Reducers> => {
    const version = parseInt(import.meta.env.VITE_APP_REDUX_PERSIST_VERSION as string);
    const initialState = getInitialState(`persistSlice:${options.name}`, options.initialState, version, nulledFields);

    const slice = createSlice({
        ...options,
        initialState,
        reducers: {
            ...options.reducers,
            _loadPersistedState: (state, action: PayloadAction<T>) => {
                return { ...state, ...action.payload };
            },
            _resetState: () => ({
                ...getInitialState(`persistSlice:${options.name}`, options.initialState, version, nulledFields)
            })
        }
    });

    sliceMap[options.name] = slice;

    const { reducer } = slice;

    const persistReducer: Reducer<T> = (state, action) => {
        const newState = reducer(state, action);
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                window.localStorage.setItem(`persistSlice:${options.name}`, JSON.stringify(newState));
            } catch (error) {
                console.error('Failed to save state to localStorage:', error);
            }
        }
        return newState;
    };

    return {
        ...slice,
        reducer: persistReducer
    };
};

export const resetPersistSlice = (name: string) => {
    if (window?.localStorage) {
        window.localStorage.removeItem(`persistSlice:${name}`);
    }
    store.dispatch(sliceMap[name].actions._resetState({}));
};

export interface IStateDefaultField<T> {
    data: T | null;
    loading: boolean;
    error: boolean;
}
export const getInitalStateDefField = <T>(data?: T): IStateDefaultField<T> => ({
    data: data || null,
    loading: false,
    error: false
});

type AsyncThunkWithRejectValue<R, A, S> = AsyncThunk<R, A, { rejectValue: S }>;

// Generic function to handle API cases
export const handleApiCases = <
    R, // Response type
    A, // Argument type for AsyncThunk
    S, // Reject value type
    IState extends Record<K, IStateDefaultField<R>>, // State type with a field
    K extends keyof Draft<IState> // Key of state field
>(
    builder: ActionReducerMapBuilder<IState>,
    stateField: K,
    asyncThunk: AsyncThunkWithRejectValue<R, A, S>,
    handlers?: {
        onPending?: (state: Draft<IState>) => void;
        onFulfilled?: (state: Draft<IState>, action: PayloadAction<R>) => void;
        onRejected?: (state: Draft<IState>, action: PayloadAction<S | undefined>) => void;
        config?: { omitDataUpdate?: boolean };
    }
) => {
    builder
        .addCase(asyncThunk.pending, state => {
            const fieldState: IStateDefaultField<R> = state[stateField as K] as IStateDefaultField<R>;
            fieldState.loading = true;
            fieldState.error = false;
            handlers?.onPending?.(state);
        })
        .addCase(asyncThunk.fulfilled, (state, action: PayloadAction<R>) => {
            const fieldState: IStateDefaultField<R> = state[stateField as K] as IStateDefaultField<R>;
            if (!handlers?.config?.omitDataUpdate) {
                fieldState.data = action.payload;
            }
            handlers?.onFulfilled?.(state, action);
            fieldState.error = false;
            fieldState.loading = false;
        })
        .addCase(asyncThunk.rejected, (state, action) => {
            const fieldState: IStateDefaultField<R> = state[stateField as K] as IStateDefaultField<R>;
            handlers?.onRejected?.(state, action);
            retryDeferMap[asyncThunk.typePrefix]?.forEach(pipeCaughtErrorToState => {
                pipeCaughtErrorToState({
                    reduxThunkType: asyncThunk.typePrefix,
                    data: action.payload as IErrorResponse
                });
            });

            fieldState.error = true;
            fieldState.loading = false;
        });
};
